import React from 'react';
import styled from 'styled-components';
import { GBLoadingState } from '@greenbackapp/ui';
import { spacingTokens, typographyTokens, mediaQueries } from '../../config/designTokens';
import { SkeletonPage, SkeletonTable, SkeletonList, SkeletonChart } from './Skeleton';
import { Breadcrumb } from './Breadcrumb';

const FallbackContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: ${spacingTokens.xxl};
  background-color: ${props => props.theme.background.default};

  ${mediaQueries.upToTablet} {
    padding: ${spacingTokens.lg};
  }
`;

const CentralLoader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 400px;
`;

const PageHeader = styled.div`
  margin-bottom: ${spacingTokens.xxl};
`;

const Title = styled.h1`
  font-family: ${typographyTokens.fontFamily.bold};
  font-size: ${typographyTokens.fontSize.display};
  font-weight: ${typographyTokens.fontWeight.bold};
  color: ${props => props.theme.text.primary};
  margin: 0 0 ${spacingTokens.xs} 0;
`;

const Subtitle = styled.p`
  font-family: ${typographyTokens.fontFamily.primary};
  font-size: ${typographyTokens.fontSize.subtitle};
  color: ${props => props.theme.text.secondary};
  margin: 0;
`;

export type LoadingVariant = 'spinner' | 'skeleton' | 'dashboard' | 'table' | 'chart' | 'list';

interface LoadingFallbackProps {
  variant?: LoadingVariant;
  message?: string;
  title?: string;
  subtitle?: string;
  breadcrumbs?: { label: string; href: string }[];
  testId?: string;
}

const SpinnerBlock: React.FC<{ message: string }> = ({ message }) => (
  <CentralLoader>
    <GBLoadingState message={message} size="large" testID="loading" />
  </CentralLoader>
);

export const LoadingFallback: React.FC<LoadingFallbackProps> = ({
  variant = 'spinner',
  message = 'Loading...',
  title,
  subtitle,
  breadcrumbs,
  testId = 'loading-fallback',
}) => {
  const renderContent = () => {
    switch (variant) {
      case 'dashboard': return <SkeletonPage hasHero hasChart listCount={3} />;
      case 'table':     return <SkeletonTable columns={5} rows={10} />;
      case 'chart':     return <SkeletonChart height="400px" />;
      case 'list':      return <SkeletonList count={8} />;
      case 'skeleton':  return <SkeletonPage hasHero={false} hasChart={false} listCount={5} />;
      default:          return <SpinnerBlock message={message} />;
    }
  };

  return (
    <FallbackContainer data-testid={testId}>
      {(title || breadcrumbs) && (
        <PageHeader>
          {breadcrumbs && <Breadcrumb items={breadcrumbs} />}
          {title && <Title>{title}</Title>}
          {subtitle && <Subtitle>{subtitle}</Subtitle>}
        </PageHeader>
      )}
      {renderContent()}
    </FallbackContainer>
  );
};

export default LoadingFallback;
